import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import { Button, Col, Container, Row } from 'react-bootstrap';
import {
  FaLongArrowAltDown,
  FaLongArrowAltUp,
  FaPlus,
  FaShareSquare,
  FaTrashAlt,
} from 'react-icons/fa';
import { concat } from 'lodash';
import Loading from '../../../components/Loading';
import { useToast } from '../../../hooks/toast';
import api from '../../../services/api';
import getResponseErrors from '../../../utils/getResponseErrors';
import getValidationErrors from '../../../utils/getValidationErrors';
import SelectAsync from '../../../components/SelectAsync';
import Input from '../../../components/Input';
import SelectSync, { IOption } from '../../../components/SelectSync';
import Toggle from '../../../components/Exame/Toggle';
import { usePerfil } from '../../../hooks/perfil';
import { useGrupoExame } from '../../../hooks/grupoExames';

// import { Container } from './styles';

interface IItens {
  GeiCodigo?: number;
  GeiOrdem: number;
  GeiGexCodigo: number;
  GeiProCodigo: number;
  procedimento?: IProcedimento;
}

interface IPerfil {
  PerCodigo: string;
  PerNome: string;
}
interface IProcedimento {
  ProCodigo: number;
  ProNome: string;
  perfil?: IPerfil;
}

export interface IGrupo {
  GexCodigo?: number;
  GexNome: string;
  GexSelecionado: boolean;
  GexAtivo: boolean;
  GexPerCodigo: number;
  perfil: IPerfil;
  itens: IItens[];
}

interface IPosicao {
  posicaoAtual: number;
  direcao: string;
  geiCodigo: number;
  geiCodigoVizinho: number;
}
type Props = {
  grupo: IGrupo | undefined;
};

const CadastroGrupoExames: React.FC<Props> = ({ grupo }) => {
  const { addToast } = useToast();
  const { optionsPerfil, buscaPerfil } = usePerfil();
  const { limparGrupo } = useGrupoExame();
  const [optionsPerfis, _] = useState<IOption[]>(optionsPerfil.optionPerfis);

  const [loading, setLoading] = useState(false);
  const formRefGrupo = useRef<FormHandles>(null);
  const formRefItens = useRef<FormHandles>(null);

  const [itens, setItens] = useState<IItens[] | undefined>(
    grupo && grupo.itens,
  );

  const [grupoLocal, setGrupoLocal] = useState<IGrupo | undefined>(grupo);
  const [codProcedimentos, setCodProcedimentos] = useState<string>('');

  const [editando, setEditando] = useState(false);

  useEffect(() => {
    async function loadPerfis(): Promise<void> {
      await buscaPerfil();
    }
    if (!optionsPerfil || Object.keys(optionsPerfil).length === 0) loadPerfis();
  }, [buscaPerfil, optionsPerfil]);

  useEffect(() => {
    const arrayItens = grupo ? grupo.itens : [];
    const arrayItensFormatado = arrayItens.map(it => {
      return {
        ...it,
        procedimento: {
          ProCodigo: it.procedimento?.ProCodigo || 0,
          ProNome: `${it.procedimento?.ProNome} - ${it.procedimento?.perfil?.PerNome}`,
        },
      };
    });
    setItens(arrayItensFormatado);
    setGrupoLocal(grupo);
  }, [grupo]);

  useEffect(() => {
    if (itens) {
      const cods = itens.map(it => it.procedimento?.ProCodigo).join(',');
      setCodProcedimentos(cods);
    }
  }, [itens]);

  const total = useMemo(() => {
    if (!itens) return 0;
    return itens.length;
  }, [itens]);

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const handleOnChangeVazio = useCallback(() => {}, []);

  const classToggle = {
    paddingRight: '3px',
    paddingLeft: '10px',
    paddingTop: '2px',
    fontWeight: 'bold',
  };

  useEffect(() => {
    if (grupo) {
      formRefGrupo.current?.setFieldValue('GexNome', grupo.GexNome);
      formRefGrupo.current?.setFieldValue('GexAtivo', grupo.GexAtivo);
      formRefGrupo.current?.setFieldValue(
        'GexSelecionado',
        grupo.GexSelecionado.toString(),
      );
      formRefGrupo.current?.setFieldValue(
        'GexPerCodigo',
        grupo.GexPerCodigo?.toString() || '',
      );
    }
  }, [grupo]);

  const handleGrupo = useCallback(
    async dados => {
      setLoading(true);
      try {
        formRefGrupo.current?.setErrors({});
        const schema = Yup.object().shape({
          GexNome: Yup.string().required('Nome do grupo obrigatório'),
        });

        await schema.validate(dados, {
          abortEarly: false,
        });

        const ativo =
          dados.GexAtivo && dados.GexAtivo !== ''
            ? dados.GexAtivo.toString()
            : 'false';

        const selecionado =
          dados.GexSelecionado && dados.GexSelecionado !== ''
            ? dados.GexSelecionado.toString()
            : 'false';

        const dadosGrupo = {
          GexNome: dados.GexNome.toUpperCase(),
          GexAtivo: ativo,
          GexSelecionado: selecionado,
          ...(dados.GexPerCodigo !== ''
            ? {
                GexPerCodigo: dados.GexPerCodigo,
              }
            : { GexPerCodigo: null }),
        };

        if (grupoLocal) {
          await api.put<IGrupo>(
            `/grupoexames/updategrupo/${grupoLocal.GexCodigo}`,
            dadosGrupo,
          );
        } else {
          const retorno = await api.post<IGrupo>(
            '/grupoexames/creategrupo',
            dadosGrupo,
          );
          setGrupoLocal({
            ...retorno.data,
          });
        }
        setEditando(false);
        limparGrupo();
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);

          formRefGrupo.current?.setErrors(errors);
          return;
        }
        const mensagemErro = getResponseErrors(error);
        addToast({
          type: 'error',
          title: 'Houve um erro ao salvar Grupo',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    },
    [addToast, grupoLocal, limparGrupo],
  );

  const handleAddProcedimento = useCallback(
    async dados => {
      setLoading(true);
      try {
        formRefItens.current?.setErrors({});
        const schema = Yup.object().shape({
          exame: Yup.string().required('Procedimento obrigatório'),
        });

        await schema.validate(dados, {
          abortEarly: false,
        });

        const dadosItens: IItens = {
          GeiGexCodigo: grupoLocal?.GexCodigo || 0,
          GeiProCodigo: dados.exame,
          GeiOrdem: itens ? itens.length + 1 : 1,
        };

        const resposta = await api.post<IItens>(
          '/grupoexames/addItem',
          dadosItens,
        );

        setCodProcedimentos(state => {
          if (state === '') return dados.exames;
          return state && concat(state, ',', dados.exames);
        });

        setItens(state => {
          return state && [...state, { ...resposta.data }];
        });
        limparGrupo();
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRefItens.current?.setErrors(errors);
          return;
        }
        const mensagemErro = getResponseErrors(error);
        addToast({
          type: 'error',
          title: 'Houve um erro ao salvar Item',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    },
    [addToast, grupoLocal, itens, limparGrupo],
  );

  const handleDeleteItem = useCallback(
    async (geiCodigo: number) => {
      setLoading(true);
      try {
        const resposta = await api.delete<boolean>(
          `/grupoexames/deleteItem/${geiCodigo}`,
        );

        if (resposta) {
          setItens(state => {
            return (
              state &&
              state
                .filter(st => st.GeiCodigo !== geiCodigo)
                .map((st, index) => {
                  return { ...st, GeiOrdem: index + 1 };
                })
            );
          });
          limparGrupo();
        }
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          type: 'error',
          title: 'Houve um erro ao remover item',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    },
    [addToast, limparGrupo],
  );

  const handlePosicao = useCallback(
    async (dados: IPosicao) => {
      setLoading(true);
      try {
        const indiceAtual =
          dados.direcao === 'up'
            ? dados.posicaoAtual - 1
            : dados.posicaoAtual + 1;
        const dadosUpdate = {
          GeiOrdem: indiceAtual,
          GeiCodigoVizinho: dados.geiCodigoVizinho,
          GeiOrdemVizinho: dados.posicaoAtual,
        };
        await api.put(
          `/grupoexames/updateordem/${dados.geiCodigo}`,
          dadosUpdate,
        );

        setItens(state => {
          const stateModificado = state?.map(st => {
            if (st.GeiCodigo === dados.geiCodigo)
              return { ...st, GeiOrdem: indiceAtual };
            if (st.GeiCodigo === dados.geiCodigoVizinho)
              return { ...st, GeiOrdem: dados.posicaoAtual };
            return { ...st };
          });

          const stateModificado2 =
            stateModificado &&
            stateModificado.sort(
              (a, b) => Number(a.GeiOrdem) - Number(b.GeiOrdem),
            );

          return stateModificado2 && [...stateModificado2];
        });
        limparGrupo();
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          type: 'error',
          title: 'Houve um erro ao remover item',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    },
    [addToast, limparGrupo],
  );

  return (
    <>
      {loading && <Loading backgroundBlack={false} />}

      <Container fluid className="mt-2">
        <div className="mb-2 p-2 bg-white border rounded">
          <Row>
            <Col md={12} style={{ textAlign: 'right' }}>
              <Button
                type="button"
                size="sm"
                variant="outline-danger"
                onClick={() => window.location.reload()}
              >
                <FaShareSquare /> Voltar para listagem
              </Button>
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <Form
                ref={formRefGrupo}
                initialData={
                  grupoLocal
                    ? {
                        GexNome: grupoLocal.GexNome,
                        GexAtivo: grupoLocal.GexAtivo ? 'true' : '',
                        GexSelecionado: grupoLocal.GexSelecionado ? 'true' : '',
                        GexPerCodigo: grupoLocal.GexPerCodigo?.toString(),
                      }
                    : {
                        GexNome: '',
                        GexAtivo: 'true',
                        GexSelecionado: '',
                      }
                }
                onSubmit={handleGrupo}
              >
                <Row>
                  <Col md={3} className="form-group pr-2">
                    <Input
                      name="GexNome"
                      label="Nome do Grupo"
                      maxLength={255}
                      disabled={!editando && grupoLocal !== undefined}
                    />
                  </Col>
                  <Col md={3} className="form-group pr-2">
                    <SelectSync
                      name="GexPerCodigo"
                      label="Perfil"
                      placeholder="Selecione"
                      handleSelectChange={handleOnChangeVazio}
                      options={optionsPerfis}
                      isDisabled={!editando && grupoLocal !== undefined}
                      isClearable
                    />
                  </Col>
                  <Col
                    md={2}
                    className="form-group pr-2"
                    style={{ alignSelf: 'end', textAlign: 'center' }}
                  >
                    <Toggle
                      nome="GexAtivo"
                      label="Ativo"
                      isDisabled={!editando && grupoLocal !== undefined}
                      classSpam={classToggle}
                    />
                  </Col>
                  <Col
                    md={2}
                    className="form-group pr-2"
                    style={{ alignSelf: 'end', textAlign: 'center' }}
                  >
                    <Toggle
                      nome="GexSelecionado"
                      label="Selecionado"
                      isDisabled={!editando && grupoLocal !== undefined}
                      classSpam={classToggle}
                    />
                  </Col>
                  <Col md={2} style={{ alignSelf: 'center' }}>
                    <div className={`d-flex justify-content-between mt-2 `}>
                      <Button
                        type="submit"
                        size="sm"
                        variant="success"
                        className={`${grupoLocal ? 'd-none' : ''}`}
                      >
                        Salvar grupo
                      </Button>

                      <Button
                        type="button"
                        size="sm"
                        variant="primary"
                        className={`${editando || !grupoLocal ? 'd-none' : ''}`}
                        onClick={() => setEditando(true)}
                      >
                        Editar
                      </Button>

                      <Button
                        type="submit"
                        size="sm"
                        variant="success"
                        className={`${!editando ? 'd-none' : ''}`}
                      >
                        Salvar
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Form>
            </Col>
          </Row>
        </div>

        <div
          className={`mb-2 p-2 bg-white border rounded ${
            !grupoLocal ? 'd-none' : ''
          }`}
        >
          <Row>
            <Col md={12}>
              <Form
                ref={formRefItens}
                initialData={{ exames: '' }}
                onSubmit={handleAddProcedimento}
              >
                <Row>
                  <Col md={10} className="form-group pr-2">
                    <SelectAsync
                      urlApi={`procedimentos/combo?${
                        codProcedimentos && codProcedimentos !== ''
                          ? `exclude=${codProcedimentos}&termoBusca=`
                          : `termoBusca=`
                      }`}
                      name="exame"
                      label="Procedimento"
                      placeholder="Procurar"
                      isClearable={false}
                      handleSelectChange={handleOnChangeVazio}
                    />
                  </Col>
                  <Col
                    md={2}
                    style={{ alignSelf: 'center', textAlign: 'center' }}
                  >
                    <div className="d-flex justify-content-between mt-2">
                      <Button type="submit" size="sm" variant="success">
                        <FaPlus />
                        ADICIONAR
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Form>
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              {grupoLocal ? (
                <>
                  <table
                    className="table table-hover table-sm table-font-small"
                    style={{ borderWidth: '0px' }}
                  >
                    <tbody>
                      <tr
                        key="topolista"
                        className="bg-light"
                        style={{ fontSize: 'Small' }}
                      >
                        <th scope="col">Nome</th>
                        <th scope="col">&nbsp;</th>
                        <th scope="col">&nbsp;</th>
                      </tr>
                      {itens &&
                        itens.map((item: IItens, index: number) => {
                          if (item.GeiCodigo)
                            return (
                              <>
                                <tr
                                  key={`itensCadastro-${
                                    item.GeiCodigo || Math.random() * 9999
                                  }`}
                                  style={{ fontSize: 'Small', height: '30px' }}
                                  data-id={item.GeiProCodigo}
                                >
                                  <td>{item.procedimento?.ProNome}</td>
                                  <td className="p-0" style={{ width: '40px' }}>
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="outline-primary"
                                      title="sobe"
                                      disabled={index + 1 === 1}
                                      onClick={() =>
                                        index - 1 > -1
                                          ? handlePosicao({
                                              direcao: 'up',
                                              geiCodigo: item.GeiCodigo || 0,
                                              posicaoAtual: index + 1,
                                              geiCodigoVizinho:
                                                itens[index - 1].GeiCodigo || 0,
                                            })
                                          : // eslint-disable-next-line prettier/prettier
                                            {}}
                                    >
                                      <FaLongArrowAltUp color="primary" />
                                    </Button>
                                  </td>
                                  <td className="p-0" style={{ width: '40px' }}>
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="outline-secondary"
                                      title="desce"
                                      key={`indice-${index + 1}`}
                                      disabled={index + 1 === total}
                                      onClick={() =>
                                        index + 1 < total
                                          ? handlePosicao({
                                              direcao: 'down',
                                              geiCodigo: item.GeiCodigo || 0,
                                              posicaoAtual: index + 1,
                                              geiCodigoVizinho:
                                                itens[index + 1].GeiCodigo || 0,
                                            })
                                          : // eslint-disable-next-line prettier/prettier
                                            ''}
                                    >
                                      <FaLongArrowAltDown color="secondary" />
                                    </Button>
                                  </td>

                                  <td className="p-0" style={{ width: '40px' }}>
                                    <Button
                                      type="button"
                                      variant="outline-danger"
                                      className="py-0"
                                      title={`excluir-${
                                        item.GeiCodigo?.toString() || ''
                                      }`}
                                      onClick={() => {
                                        handleDeleteItem(item.GeiCodigo || 0);
                                      }}
                                    >
                                      <FaTrashAlt />
                                    </Button>
                                  </td>
                                </tr>
                              </>
                            );
                          return <span />;
                        })}
                    </tbody>
                  </table>
                </>
              ) : (
                ''
              )}
            </Col>
          </Row>
        </div>
      </Container>
    </>
  );
};

export default CadastroGrupoExames;
